import { useRef, type FocusEvent } from 'react';
import { useIntl } from 'react-intl';

import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { getI18nValidationState } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { ValidationState } from '../validation/types.js';
import { containerRoot } from './useContainerFocus.sty.js';

/** @internal */
interface UseContainerFocusOptions<T extends HTMLElement = HTMLElement> {
  onFocus?: (e: FocusEvent<T>) => void;
  onBlur?: (e: FocusEvent<T>) => void;
  disabled?: boolean;
  emitValidationState?: (state: ValidationState) => void;
  /**
   * When provided, called on blur instead of the default
   * `getI18nValidationState(intl, control, 'blur')` to produce the
   * ValidationState passed to `emitValidationState`. Use when the component
   * has richer error state than native input.validity can express.
   */
  getBlurValidationState?: () => ValidationState;
  /**
   * When set, elements inside a portaled overlay tagged with
   * `data-dt-owner="{overlayOwnerId}"` are treated as part of the component
   * boundary. Focus moving between the container and the overlay does not
   * fire onFocus, onBlur, or SETISTOUCHED.
   */
  overlayOwnerId?: string;
  /**
   * When provided, called with the blur relatedTarget before SETISTOUCHED is
   * dispatched. Return true to suppress the blur (no SETISTOUCHED, no onBlur).
   *
   * TODO: [APPDEV-19074] Temporary escape hatch for the TimeframeSelector overlay — clicking
   * preset/recent items shifts focus outside the picker boundary, which causes
   * SETISTOUCHED to fire and the resulting layout shift makes the click miss.
   * Remove once the layout shift is resolved at the root.
   */
  shouldSuppressBlur?: (relatedTarget: Element | null) => boolean;
}

/**
 * Handles onFocus / onBlur for composite container components:
 * - Fires onFocus only on the first focusin (deduplicates internal focus moves).
 * - Fires onBlur only when focus genuinely leaves the container (and portaled overlay, if any).
 * - Dispatches SETISTOUCHED when the whole component boundary is left.
 * - Uses tabIndex={-1} to prevent focus escaping to document.body on clicks to non-focusable areas.
 * @internal
 */
function isWithinOverlay(
  node: EventTarget | null,
  overlayOwnerId: string | undefined,
): boolean {
  return (
    !!overlayOwnerId &&
    node instanceof HTMLElement &&
    !!node.closest(`[data-dt-owner="${overlayOwnerId}"]`)
  );
}

function isInsideBoundary(
  node: EventTarget | null,
  container: HTMLElement,
  overlayOwnerId: string | undefined,
): boolean {
  return (
    container.contains(node as Node | null) ||
    isWithinOverlay(node, overlayOwnerId)
  );
}

/** @internal */
export function useContainerFocus<T extends HTMLElement = HTMLElement>({
  onFocus,
  onBlur,
  disabled,
  emitValidationState,
  getBlurValidationState,
  overlayOwnerId,
  shouldSuppressBlur,
}: UseContainerFocusOptions<T>) {
  const { dispatch, state } = useFormFieldMessagesContext();
  const intl = useIntl();

  // Stays true while focus is anywhere inside the container (or portaled overlay).
  const hasFocusRef = useRef(false);

  function handleFocus(e: FocusEvent<T>) {
    if (disabled) {
      return;
    }
    if (!hasFocusRef.current) {
      hasFocusRef.current = true;
      onFocus?.(e);
    }
  }

  function handleBlur(e: FocusEvent<T>) {
    if (disabled) {
      // Reset the focus flag when focus genuinely leaves while disabled so that
      // the next focusin after re-enabling correctly fires onFocus again.
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        hasFocusRef.current = false;
      }
      return;
    }

    if (isInsideBoundary(e.relatedTarget, e.currentTarget, overlayOwnerId)) {
      return;
    }

    if (shouldSuppressBlur?.(e.relatedTarget as Element | null)) {
      return;
    }

    // container has tabIndex={-1}, so clicks on non-focusable areas land focus
    // on the container itself — container.contains(container) is true, so those
    // are swallowed here and never reach onBlur.
    hasFocusRef.current = false;
    const control = e.currentTarget.querySelector<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >('input, textarea, select');
    // `checkValidity()` (not `reportValidity()`) fires the `invalid` event the
    // DS validation relies on without the browser's user-facing reporting —
    // `reportValidity()` refocuses the invalid control and shows the native
    // bubble, which steals focus back on blur when an external validator (e.g.
    // react-hook-form via `controlState`) leaves the `invalid` event
    // uncancelled, trapping the user in the field.
    control?.checkValidity();
    dispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: true },
    });
    if (emitValidationState && !state.isTouched) {
      emitValidationState(
        getBlurValidationState?.() ??
          getI18nValidationState(intl, control ?? null, 'blur'),
      );
    }
    onBlur?.(e);
  }

  return {
    containerClassName: containerRoot,
    onFocus: handleFocus,
    onBlur: handleBlur,
    tabIndex: -1,
  };
}
